import { useMemo } from 'react'
import type { ScheduleEventDto, EmployeeDto, UnavailabilityDto } from '../../../../lib/api/v2/planning'
import { EventBar } from './EventBar'
import {
  agendaDays, startOfWeek, isoDate, eventOnDay, isoWeek, brusselsTodayIso, isWeekendDay,
  poseCapacityForDay, poseLoadOf, formatPoseLoad, sortCellEvents, buildAgendaLanes,
  buildTypeLanes, abbreviateAssignees, assigneeFullNames,
  type ProjectAlertBadges, type AlertBadge, type DayCapacity, type AgendaLane, type AgendaLaneMode,
} from '../planning-helpers'
import { planningSettings } from '../planning-settings'
import { cn } from '../../../../lib/utils'

interface AgendaGridProps {
  startDate: Date
  weekCount?: number
  events: ScheduleEventDto[]
  employees: EmployeeDto[]
  /**
   * How lanes are grouped (SOLY-PLAN-005). « crew » shows one lane per crew instead of one per
   * member — the same bar stops being drawn once per assignee. Capacity gauges are unaffected in
   * both modes: they count people, and a view never changes a fact.
   * « type » shows one lane per kind of work (poses, métrés, RDV) — each event drawn once.
   */
  laneMode?: AgendaLaneMode
  unavailabilities?: UnavailabilityDto[]
  holidays?: Set<string>
  /** Open Suivi points per project, mapped to the work item carrying them (badge on the bar). */
  alertsByProject?: Record<string, ProjectAlertBadges>
  /**
   * The slot the agenda was just pointed at from the cockpit (« voir dans l'agenda »): it wears a
   * ring for a moment. Without it, a jump landing on the week already displayed reads as a dead
   * button — nothing on screen moves.
   */
  highlightEventId?: string | null
  /** Click on an event bar → open the project detail drawer (MS-22). */
  onEventClick?: (e: ScheduleEventDto) => void
  /** Click on an event's hover pencil → open the scheduling fiche (edit) — MS-22. */
  onEventEdit?: (e: ScheduleEventDto) => void
  /** Open the scheduling fiche from an empty cell (double-click or the hover ＋) — MS-18. */
  onQuickAdd?: (args: { userId: string | null; day: Date; startTime: string; x: number; y: number }) => void
  /** Open the add-absence popover (MS-04) from an employee row. */
  onAddAbsence?: (args: { userId: string; label: string; x: number; y: number }) => void
  /** Click on an absence cell → open the absence detail popover (view / edit / delete). */
  onAbsenceClick?: (args: { absence: UnavailabilityDto; x: number; y: number }) => void
}

/**
 * The single person a lane's gestures can act on, or null when there is no unambiguous one.
 *
 * The unassigned tray has no owner by design (its ＋ creates with no assignee), and a crew row of
 * two or more has no single one — so both answer null, for different reasons the caller does not
 * need to tell apart. Keyed on the ACTIVE members: a deactivated colleague sharing the row cannot
 * make the living one lose their ＋, which is precisely how that bug came back a second time.
 */
function soleOwnerOf(lane: AgendaLane): string | null {
  return (lane.kind === 'person' || lane.kind === 'crew') && lane.activeMemberIds.length === 1 ? lane.activeMemberIds[0] : null
}

/** What makes a cell unavailable: a public holiday, an employee absence, or both. */
interface CellUnavailability { holiday: boolean; absence?: UnavailabilityDto }

interface CellProps {
  unavailable: boolean
  today?: boolean
  weekend?: boolean
  children: React.ReactNode
  onDoubleClick?: (e: React.MouseEvent<HTMLDivElement>) => void
  testId: string
}

function Cell({ unavailable, today, weekend, children, onDoubleClick, testId }: CellProps) {
  return (
    <div
      data-testid={testId}
      data-unavailable={unavailable}
      onDoubleClick={onDoubleClick}
      className={cn(
        'group/cell relative min-h-[64px] space-y-1 border-b border-r p-1',
        unavailable && 'bg-[repeating-linear-gradient(45deg,#f3f4f6,#f3f4f6_6px,#e5e7eb_6px,#e5e7eb_12px)]',
        // Today = framed column (inset shadows: no layout shift) + light tint — V3 cockpit.
        // The inset edges were rgba(37,99,235,…) — the OLD shadcn blue, as a raw literal, so the
        // theme audit (which grepped Tailwind `-blue-` classes) walked straight past them and left
        // today's column with an ink fill and blue borders.
        !unavailable && today && 'bg-primary/5 shadow-[inset_2px_0_0_rgba(15,23,42,0.35),inset_-2px_0_0_rgba(15,23,42,0.35)]',
        !unavailable && !today && weekend && 'bg-muted/40',
      )}
    >
      {children}
    </div>
  )
}

/**
 * Agenda grid: days in columns × employees in rows (+ an "Unassigned" row). Events appear on the
 * row of each assigned user and the columns of the days they span. Placement is via the scheduling
 * fiche (click) — drag&drop was removed in MS-18. SOLY-PLAN-001 MS-03/18/19.
 */
export function AgendaGrid({
  startDate, weekCount = 2, events, employees, laneMode = 'person',
  unavailabilities = [], holidays,
  alertsByProject = {}, highlightEventId, onEventClick, onEventEdit, onQuickAdd, onAddAbsence, onAbsenceClick,
}: AgendaGridProps) {
  // Mon→Sat columns; a Sunday appears only when an event lands on it (round 6 day policy).
  const days = useMemo(() => {
    const occupiedSundays = new Set<string>()
    const cursor = new Date(startOfWeek(startDate))
    for (let w = 0; w < weekCount; w++) {
      cursor.setUTCDate(cursor.getUTCDate() + 6) // the Sunday of this week
      if (events.some((e) => eventOnDay(e, cursor))) {
        occupiedSundays.add(isoDate(cursor))
      }
      cursor.setUTCDate(cursor.getUTCDate() + 1)
    }
    return agendaDays(startDate, weekCount, occupiedSundays)
  }, [startDate, weekCount, events])

  const weekChunks = useMemo(() => {
    const out: Date[][] = []
    let currentWeek = ''
    for (const d of days) {
      const key = isoDate(startOfWeek(d))
      if (key !== currentWeek) { out.push([]); currentWeek = key }
      out[out.length - 1].push(d)
    }
    return out
  }, [days])

  // « Non assigné » OPENS the grid (Élodie 2026-08-18). It used to close it, filed after the staff
  // as if it were one more person. It is not a person, it is the arrival tray: what landed on the
  // agenda and belongs to nobody yet is the first thing to look at in the morning, and the top of
  // the grid is the only place that never scrolls out of reach.
  //
  // Then, per lane mode: the staff grouped by function (Placeur → Métreur → Commercial → sans
  // fonction), or one lane per crew followed by the people in none (SOLY-PLAN-005). The order IS the
  // grouping: contiguous blocks read as groups without spending a header row of vertical space in an
  // already dense grid.
  const lanes = useMemo(
    () => (laneMode === 'type' ? buildTypeLanes(events, days) : buildAgendaLanes(employees, laneMode)),
    [employees, laneMode, events, days],
  )
  // Names for the « Types » bars. The roster already holds a deactivated member while one of
  // their events is loaded, so a bar never loses a name to a departure.
  const nameById = useMemo(
    () => new Map(employees.map((e) => [e.userId, e.displayName || e.email])),
    [employees],
  )

  // A crew lane carries no absence (SOLY-PLAN-005, D6): an absence belongs to a PERSON, and hatching
  // a whole crew because one of its two members is off would be false. The férié stays — it is a
  // fact about the day, true for everyone on the row. Who is off is one toggle away, in the person
  // view, where the ＋ that records it also lives.
  const unavailabilityFor = (lane: AgendaLane, day: Date): CellUnavailability => {
    const owner = soleOwnerOf(lane)
    return {
      holiday: holidays?.has(isoDate(day)) ?? false,
      absence: owner
        ? unavailabilities.find(
            (u) => u.userId === owner && eventOnDay(u, day),
          )
        : undefined,
    }
  }

  // One `filter` per event, so an event shared by two people of the SAME lane is rendered once —
  // the duplication this feature removes disappears by construction, not by a de-dup pass.
  const rawEventsFor = (lane: AgendaLane, day: Date): ScheduleEventDto[] =>
    events.filter((e) => {
      if (!eventOnDay(e, day)) return false
      if (lane.kind === 'unassigned') return e.assignments.length === 0
      // A type lane holds EVERY event of its kind, assigned or not.
      if (lane.kind === 'type') return e.kind === lane.eventKind
      return e.assignments.some((a) => lane.memberIds.includes(a.assignedUserId))
    })

  // Day-unit tasks first (the cell's base) so hour-unit tasks render "posées dessus" (MS-19), THEN
  // by start time — one lane, one day, read in the order the crew lives it (Élodie 2026-08-18).
  const eventsFor = (lane: AgendaLane, day: Date): ScheduleEventDto[] => sortCellEvents(rawEventsFor(lane, day))
  const todayIso = brusselsTodayIso()

  // Pose capacity (V3 cockpit): ACTIVE installers only — a deactivated member
  // may still own a lane (assigned events, SOLY-TEAM-001) but adds no capacity.
  const installerIds = useMemo(
    () => employees.filter((e) => e.isActive && e.skills.includes('Installer')).map((e) => e.userId),
    [employees],
  )
  const isNonWorking = (d: Date) => isWeekendDay(d) || (holidays?.has(isoDate(d)) ?? false)
  const capacityFor = (d: Date): DayCapacity | null =>
    !isNonWorking(d) && installerIds.length > 0
      ? poseCapacityForDay(d, installerIds, events, unavailabilities)
      : null
  // Week summary = placeur-days CONSUMED / available, over the working days. Consumed follows the
  // same rule as the segments (whole day = 1, hour tasks = ½) — counting only whole-day tasks made a
  // week of hourly poses read « 0 % » while its gauges were half-full (SOLY-FIX-004).
  const weekCapacity = (chunk: Date[]) => {
    let load = 0
    let avail = 0
    for (const d of chunk) {
      const c = capacityFor(d)
      if (c) { load += poseLoadOf(c); avail += c.available }
    }
    return avail > 0 ? { load, avail, pct: Math.round((load / avail) * 100) } : null
  }

  // No overflow here: the page provides the single scroll container, so the sticky header rows
  // (week band at top-0, day headers at top-7) stay visible while the body scrolls. Header cells
  // use a SOLID bg (not /30) — content scrolls underneath them.
  return (
    <div data-testid="planning-agenda-grid">
      <div
        className="grid min-w-[900px]"
        style={{ gridTemplateColumns: `160px repeat(${days.length}, minmax(120px, 1fr))` }}
      >
        {/* week-band row — softened control-tower band carrying the week's pose capacity. The V3
            mockup's slate-900 slab proved harsh on the eye in daily use; slate-600 keeps the dark
            week anchor at a livable contrast (variant D, picked live by Thierry 2026-06-10). */}
        <div className="sticky left-0 top-0 z-30 h-7 border-b border-r bg-muted" />
        {weekChunks.map((chunk, wi) => {
          const wcap = weekCapacity(chunk)
          return (
            <div
              key={wi}
              className="sticky top-0 z-20 flex h-7 items-center justify-between border-b border-l border-l-slate-500 bg-slate-600 px-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-200"
              style={{ gridColumn: `span ${chunk.length}` }}
            >
              <span>Semaine {isoWeek(chunk[0])}</span>
              {wcap && (
                <span className="normal-case tracking-normal text-white" data-testid={`planning-week-capacity-${isoWeek(chunk[0])}`}>
                  Capacité pose {formatPoseLoad(wcap.load)}/{wcap.avail} j · {wcap.pct} %
                </span>
              )}
            </div>
          )
        })}

        {/* day-header row */}
        {/* The column names what the rows ARE — read off the lanes actually rendered, not off the
            mode that was asked for. An org whose only crew has nothing to group is in crew mode with
            no crew row in sight, and a column headed « Équipes » above a list of people is exactly
            the kind of small lie that makes a reader distrust the rest of the screen. */}
        <div className="sticky left-0 top-7 z-30 border-b border-r bg-muted p-2 text-xs font-semibold">
          {laneMode === 'type' ? 'Types' : lanes.some((l) => l.kind === 'crew') ? 'Équipes' : 'Équipe'}
        </div>
        {days.map((d) => {
          const isToday = isoDate(d) === todayIso
          const cap = capacityFor(d)
          const nonWorkingCount = isNonWorking(d)
            ? events.filter((e) => eventOnDay(e, d)).length
            : 0
          return (
            <div
              key={isoDate(d)}
              data-today={isToday || undefined}
              className={cn(
                'sticky top-7 z-20 border-b border-r px-1.5 pb-1 pt-1.5 text-center text-xs font-semibold',
                isToday ? 'bg-primary text-primary-foreground' : 'bg-muted',
                isWeekendDay(d) && !isToday && 'text-muted-foreground/70',
              )}
            >
              {d.toLocaleDateString('fr-BE', { weekday: 'short', day: '2-digit', month: '2-digit', timeZone: 'UTC' })}
              {/* capacity gauge: one segment per available placeur — filled = whole-day task,
                  half = hour tasks only; absents leave the denominator (honest by construction) */}
              {cap && cap.available > 0 && (
                <>
                  <div className="mx-1 mt-1 flex h-[5px] gap-0.5" data-testid={`planning-day-gauge-${isoDate(d)}`} aria-hidden>
                    {Array.from({ length: cap.available }, (_, i) => (
                      <span
                        key={i}
                        className={cn('flex-1 rounded-sm',
                          i < cap.occupied
                            ? (isToday ? 'bg-white' : 'bg-primary')
                            : i < cap.occupied + cap.partial
                              ? (isToday ? 'bg-white/60' : 'bg-primary/40')
                              : (isToday ? 'bg-white/25' : 'bg-slate-300/80'))}
                      />
                    ))}
                  </div>
                  <div
                    data-testid={`planning-day-count-${isoDate(d)}`}
                    className={cn('mt-0.5 text-[9.5px] font-medium', isToday ? 'text-primary-foreground/90' : 'text-muted-foreground')}
                  >
                    {/* Same rule as the segments above: an hourly pose is half a placeur-day, not
                        zero. The unit is « j » like the week band, NOT « placeurs » : the numerator
                        counts placeur-DAYS, so « 0,5/2 placeurs » would have been a half-person.
                        Who is busy is already answered by the segments, one per placeur. */}
                    {formatPoseLoad(poseLoadOf(cap))}/{cap.available} j{isToday ? " · aujourd'hui" : ''}
                    {cap.absent > 0 && ` · ${cap.absent} absent${cap.absent > 1 ? 's' : ''}`}
                  </div>
                </>
              )}
              {cap && cap.available === 0 && (
                <div className="mt-0.5 text-[9.5px] font-medium text-muted-foreground">
                  {cap.absent} absent{cap.absent > 1 ? 's' : ''}
                </div>
              )}
              {!cap && (
                <div className={cn('mt-0.5 text-[9.5px] font-medium', isToday ? 'text-primary-foreground/90' : 'text-muted-foreground')}>
                  {isNonWorking(d) ? `non ouvré${nonWorkingCount > 0 ? ` · ${nonWorkingCount} RDV` : ''}` : ' '}
                </div>
              )}
            </div>
          )
        })}

        {/* body rows */}
        {lanes.map((lane) => (
          <PlanningRow key={lane.key} lane={lane} days={days} todayIso={todayIso} nameById={nameById}
            unavailabilityFor={unavailabilityFor} eventsFor={eventsFor} alertsByProject={alertsByProject}
            highlightEventId={highlightEventId}
            onEventClick={onEventClick} onEventEdit={onEventEdit}
            onQuickAdd={onQuickAdd} onAddAbsence={onAddAbsence} onAbsenceClick={onAbsenceClick} />
        ))}
      </div>
    </div>
  )
}

interface PlanningRowProps {
  lane: AgendaLane
  /** Assignee names, for the « Types » bars, whose lane no longer says who. */
  nameById: ReadonlyMap<string, string>
  days: Date[]
  todayIso: string
  unavailabilityFor: (lane: AgendaLane, day: Date) => CellUnavailability
  eventsFor: (lane: AgendaLane, day: Date) => ScheduleEventDto[]
  alertsByProject: Record<string, ProjectAlertBadges>
  highlightEventId?: string | null
  onEventClick?: (e: ScheduleEventDto) => void
  onEventEdit?: (e: ScheduleEventDto) => void
  onQuickAdd?: (args: { userId: string | null; day: Date; startTime: string; x: number; y: number }) => void
  onAddAbsence?: (args: { userId: string; label: string; x: number; y: number }) => void
  onAbsenceClick?: (args: { absence: UnavailabilityDto; x: number; y: number }) => void
}

/** The org work day's opening time as « HH:mm » — what the ＋ on an occupied cell starts from. */
function dayStartTime(): string {
  return `${String(planningSettings().workDayStartHour).padStart(2, '0')}:00`
}

/**
 * Map the vertical click position inside a day cell to a start time, « HH:mm ».
 *
 * The range used to be 8h..17h written here in figures, while the org's reference day is 7h→16h
 * (served by GET /planning/settings since MS-05): a click at the very top of a cell prefilled 8h,
 * an hour the day does not even start at, and the last band offered 17h, an hour it no longer
 * covers. Two constants for one work day is exactly what MS-05 removed everywhere else.
 */
function startTimeFromClick(e: React.MouseEvent<HTMLElement>): string {
  const { workDayStartHour, workDayEndHour } = planningSettings()
  const rect = e.currentTarget.getBoundingClientRect()
  const ratio = rect.height > 0 ? (e.clientY - rect.top) / rect.height : 0
  // The LAST hour a slot can START inside the day is one before it ends: clamping to workDayEndHour
  // itself opened the fiche already flagged « hors des heures de travail », the app disagreeing with
  // its own prefill.
  const lastStartHour = Math.max(workDayStartHour, workDayEndHour - 1)
  const bands = Math.max(1, lastStartHour - workDayStartHour + 1)
  const hour = Math.min(lastStartHour, Math.max(workDayStartHour, workDayStartHour + Math.floor(ratio * bands)))
  return `${String(hour).padStart(2, '0')}:00`
}

function PlanningRow({ lane, nameById, days, todayIso, unavailabilityFor, eventsFor, alertsByProject, highlightEventId, onEventClick, onEventEdit, onQuickAdd, onAddAbsence, onAbsenceClick }: PlanningRowProps) {
  const isCrew = lane.kind === 'crew'
  const isType = lane.kind === 'type'
  // The RDV lane keeps the ＋: quick-add creates a RDV, so this is the one type lane where the
  // gesture says what it does. Unassigned, like the tray's — the fiche asks who.
  const isRdvLane = isType && lane.eventKind === 'Appointment'
  // Gestures follow the UNAMBIGUOUS TARGET, not the lane kind: a ＋ has to know whom it assigns and
  // an absence has to know whose it is. A crew row of two therefore carries neither (D1 — it adds a
  // way of LOOKING, not a gesture to unlearn if the feature is withdrawn), while any row with a
  // single active owner keeps everything it had.
  const laneUserId = soleOwnerOf(lane)
  // Badge = the Suivi points anchored to THIS work item (métré bar ≠ pose bar); RDV carry none.
  const alertFor = (e: ScheduleEventDto): AlertBadge | undefined => {
    if (!e.projectId) return undefined
    const badges = alertsByProject[e.projectId]
    if (!badges) return undefined
    if (e.kind === 'TechnicalSurvey') return badges.metre
    if (e.kind === 'Installation') return badges.pose
    return undefined
  }
  return (
    <>
      <div
        className="group/row sticky left-0 z-10 flex items-center justify-between border-b border-r bg-background p-2"
        // ⚠️ A crew lane must NEVER wear the `planning-row-` prefix: TC-019 and TC-031 read a USER
        // id off the first such testid that is not « unassigned ».
        // Same rule for a type lane: `planning-lane-type-<kind>`.
        data-testid={isCrew ? `planning-lane-crew-${lane.key.slice('crew:'.length)}`
          : isType ? `planning-lane-type-${lane.eventKind}`
          : `planning-row-${lane.key}`}
        data-lane-kind={lane.kind}
      >
        {/* Name over its sub-line: with the lanes ordered by function (or named after a crew), the
            sub-label is what tells the reader WHICH block they are in — no header row, no vertical
            space spent. On a crew it lists the members: the only place that says who is in it
            without a click. */}
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-medium" title={lane.label}>{lane.label}</span>
          {/* The sub-label testid is NOT prefixed `planning-row-`: TC-019 selects lanes with
              [data-testid^="planning-row-"] and takes .first(), so a nested element sharing that
              prefix is a trap. */}
          {lane.sublabel && (
            <span className="truncate text-[10px] leading-tight text-muted-foreground"
              title={isCrew ? lane.sublabel : undefined}
              data-testid={`planning-lane-functions-${lane.key}`}>
              {lane.sublabel}
            </span>
          )}
        </span>
        {laneUserId && onAddAbsence && (
          <button
            type="button"
            title="Ajouter une absence"
            data-testid={`planning-add-absence-${laneUserId}`}
            onClick={(e) => onAddAbsence({ userId: laneUserId, label: lane.label, x: e.clientX, y: e.clientY })}
            className="ml-2 flex h-5 w-5 flex-none items-center justify-center rounded text-muted-foreground opacity-0 transition hover:bg-muted group-hover/row:opacity-100"
          >
            ＋
          </button>
        )}
      </div>
      {days.map((d) => {
        const { holiday, absence } = unavailabilityFor(lane, d)
        const unavailable = holiday || absence !== undefined
        const cellEvents = eventsFor(lane, d)
        // Adding is allowed on occupied cells too (métré 8h-10h + RDV 14h, hours over a day task…):
        // the BE validates real collisions (MS-17) — the agenda must not pre-block what it allows.
        // Holidays stay addable (a RDV is legitimate there — day policy round 6); absences block.
        // Adding needs an unambiguous assignee — the tray (nobody) or a row with a single active
        // owner. A crew row of two would have to guess (D1).
        const canQuickAdd = (lane.kind === 'unassigned' || isRdvLane || laneUserId !== null)
          && absence === undefined && !!onQuickAdd
        const isEmpty = cellEvents.length === 0
        const fire = (e: React.MouseEvent<HTMLElement>, startTime?: string) => onQuickAdd!({
          userId: laneUserId,
          day: d, startTime: startTime ?? startTimeFromClick(e), x: e.clientX, y: e.clientY,
        })
        return (
          <Cell key={`${lane.key}-${isoDate(d)}`} testId={`planning-cell-${lane.key}|${isoDate(d)}`} unavailable={unavailable}
            today={isoDate(d) === todayIso} weekend={isWeekendDay(d)} onDoubleClick={canQuickAdd && isEmpty ? fire : undefined}>
            {/* reason label, light and centred over the stripes; absence opens the detail popover */}
            {holiday && !absence && (
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[11px] italic text-muted-foreground/70">
                Férié
              </span>
            )}
            {absence && (
              <button
                type="button"
                title="Voir l'absence"
                data-testid={`planning-absence-cell-${lane.key}|${isoDate(d)}`}
                onClick={(e) => onAbsenceClick?.({ absence, x: e.clientX, y: e.clientY })}
                className="absolute inset-0 flex items-center justify-center text-[11px] italic text-muted-foreground/80 transition hover:text-foreground"
              >
                {absence.label}
              </button>
            )}
            {cellEvents.map((e) => (
              <EventBar key={e.id} event={e} day={d} alert={alertFor(e)} highlighted={e.id === highlightEventId}
                onClick={onEventClick} onEdit={onEventEdit}
                assigneesLine={isType ? abbreviateAssignees(e.assignments.map((a) => a.assignedUserId), nameById) : undefined}
                tooltipExtra={isType && e.assignments.length > 0 ? assigneeFullNames(e, nameById) : undefined} />
            ))}
            {canQuickAdd && isEmpty && (
              <button
                type="button"
                aria-label="Ajouter un événement"
                data-testid={`planning-cell-add-${lane.key}|${isoDate(d)}`}
                onClick={fire}
                className="absolute inset-0 flex items-center justify-center text-lg text-muted-foreground/50 opacity-0 transition hover:bg-muted/40 group-hover/cell:opacity-100"
              >
                ＋
              </button>
            )}
            {canQuickAdd && !isEmpty && (
              <button
                type="button"
                aria-label="Ajouter un événement"
                data-testid={`planning-cell-add-${lane.key}|${isoDate(d)}`}
                onClick={(e) => fire(e, dayStartTime())}
                className="relative flex h-5 w-full items-center justify-center rounded text-sm leading-none text-muted-foreground/60 opacity-0 transition hover:bg-muted/60 group-hover/cell:opacity-100"
              >
                ＋
              </button>
            )}
          </Cell>
        )
      })}
    </>
  )
}
